// Use environment variable with fallback to localhost:5001 
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    console.log('API Service initialized with base URL:', this.baseURL);
    console.log('Environment check - REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.log('Environment check - NODE_ENV:', process.env.NODE_ENV);
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'API request failed');
      }

      return data.data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Content endpoints
  async getLatestMovies() {
    return this.makeRequest('/movies');
  }

  async getLatestTVShows() {
    return this.makeRequest('/tvshows');
  }

  async getTrendingContent() {
    return this.makeRequest('/trending');
  }

  async searchContent(query) {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const encodedQuery = encodeURIComponent(query.trim());
    return this.makeRequest(`/search?q=${encodedQuery}`);
  }

  async ping() {
    return this.makeRequest('/ping');
  }

  // Health check
  async checkHealth() {
    try {
      const response = await fetch(`${this.baseURL}/health`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  // Cache management
  async getCacheStatus() {
    return this.makeRequest('/cache/status');
  }

  async clearCache() {
    return this.makeRequest('/cache/clear', { method: 'POST' });
  }

  async getScrapedContent() {
    // Redirect to enhanced scrape endpoint (POST method for fresh scraping)
    console.log('🔄 getScrapedContent redirecting to enhanced scrape endpoint');
    const response = await this.triggerScrape();
    
    // Transform response to match expected format
    return {
      data: response.data,
      debug: response.breakdown || response.debugInfo || {}
    };
  }

  async getContentSources(contentId) {
    console.log(`[ApiService] Fetching sources for content ID: ${contentId}`);
    console.log(`[ApiService] Full URL will be: ${this.baseURL}/content/${contentId}/sources`);
    return this.makeRequest(`/content/${contentId}/sources`);
  }

  // Firebase endpoints
  async getFirebaseMovies(limit = 50, offset = 0) {
    console.log(`[ApiService] Fetching Firebase movies: limit=${limit}, offset=${offset}`);
    const response = await this.makeRequest(`/firebase/movies?limit=${limit}&offset=${offset}`);
    // Response now contains { movies: [...], count: x, hasMore: boolean }
    return response;
  }

  async getTotalMoviesCount() {
    console.log(`[ApiService] Fetching total movie count`);
    return this.makeRequest(`/firebase/movies?count=true`);
  }

  async getAllMoviesUnlimited() {
    console.log(`[ApiService] Fetching ALL movies (WARNING: Memory intensive)`);
    return this.makeRequest(`/firebase/movies?all=true`);
  }

  async getMovieById(movieId) {
    const rawMovie = await this.makeRequest(`/firebase/movies?id=${movieId}&single=true`);
    if (rawMovie) {
      // Check if data is already transformed (has top-level year, source, etc.)
      const isAlreadyTransformed = rawMovie.year !== undefined || 
                                   rawMovie.source !== undefined || 
                                   rawMovie.description !== undefined;
      
      if (isAlreadyTransformed) {
        console.log(`[ApiService] Movie ${movieId} already transformed, returning as-is`);
        return rawMovie; // Return as-is
      } else {
        console.log(`[ApiService] Movie ${movieId} is raw Firebase data, transforming`);
        console.log(`[ApiService] Raw movie data structure:`, JSON.stringify(rawMovie, null, 2));
        // Transform raw Firebase data
        const transformed = this.transformFirebaseToNetflix([rawMovie]);
        console.log(`[ApiService] Transformed movie data:`, JSON.stringify(transformed[0], null, 2));
        return transformed[0] || null;
      }
    }
    return null;
  }

  async updateFirebaseMovie(movieId, updates) {
    const url = `${this.baseURL}/firebase/update-movie`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          movieId,
          title: updates.title,
          type: updates.type,
          posterUrl: updates.posterUrl,
          year: updates.year
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Update failed');
      }

      return fullResponse;
    } catch (error) {
      console.error('Update movie failed:', error);
      throw error;
    }
  }

  async refreshMetadata(movieId) {
    const url = `${this.baseURL}/firebase/update-movie`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          movieId,
          refreshMetadata: true,
          timestamp: Date.now() // Cache busting
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();
      
      console.log(`[API DEBUG] Full refresh response:`, fullResponse);
      console.log(`[API DEBUG] Backend debug info:`, fullResponse.debug);
      if (fullResponse.debug) {
        console.log(`[API DEBUG] TMDB Data:`, fullResponse.debug.tmdbData || 'No TMDB data');
        console.log(`[API DEBUG] Refresh Result:`, fullResponse.debug.refreshResult || 'No refresh result');
      }

      if (!fullResponse.success) {
        console.error(`[API DEBUG] Refresh failed:`, fullResponse);
        throw new Error(fullResponse.error || 'Refresh failed');
      }

      return fullResponse.data;
    } catch (error) {
      console.error('Refresh metadata failed:', error);
      throw error;
    }
  }

  // Batch refresh metadata for multiple movies
  async batchRefreshMetadata(movieIds, onProgress = null) {
    const results = {
      total: movieIds.length,
      successful: 0,
      failed: 0,
      errors: []
    };

    console.log(`[Batch Refresh] Starting batch metadata refresh for ${movieIds.length} movies`);

    for (let i = 0; i < movieIds.length; i++) {
      const movieId = movieIds[i];
      try {
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: movieIds.length,
            movieId,
            status: 'processing'
          });
        }

        await this.refreshMetadata(movieId);
        results.successful++;
        
        console.log(`[Batch Refresh] ✅ ${i + 1}/${movieIds.length} - Successfully refreshed: ${movieId}`);

        // Small delay to avoid overwhelming the APIs
        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error) {
        results.failed++;
        results.errors.push({ movieId, error: error.message });
        console.error(`[Batch Refresh] ❌ ${i + 1}/${movieIds.length} - Failed to refresh: ${movieId}`, error.message);
      }
    }

    console.log(`[Batch Refresh] Complete! ${results.successful} successful, ${results.failed} failed`);
    return results;
  }

  // Fix ALL movies - loads all movies progressively and fixes suspicious ratings
  async fixAllMoviesProgressive(onProgress = null) {
    const BATCH_SIZE = 100; // Load movies in batches of 100
    const results = {
      totalMovies: 0,
      totalScanned: 0,
      totalSuspicious: 0,
      totalFixed: 0,
      totalFailed: 0,
      errors: [],
      phases: {
        loading: { completed: false, progress: 0 },
        scanning: { completed: false, progress: 0 },
        fixing: { completed: false, progress: 0 }
      }
    };

    try {
      // Phase 1: Get total count for progress tracking
      console.log(`[Fix ALL] Phase 1: Getting total movie count...`);
      if (onProgress) {
        onProgress({
          phase: 'counting',
          status: 'Getting total movie count...',
          results
        });
      }

      const countResponse = await this.getTotalMoviesCount();
      results.totalMovies = countResponse.totalCount;
      console.log(`[Fix ALL] Found ${results.totalMovies} total movies`);

      // Phase 2: Load and scan movies in batches
      console.log(`[Fix ALL] Phase 2: Loading and scanning movies in batches of ${BATCH_SIZE}...`);
      let offset = 0;
      let hasMore = true;
      let allSuspiciousMovies = [];

      while (hasMore) {
        if (onProgress) {
          results.phases.loading.progress = Math.min(100, (results.totalScanned / results.totalMovies) * 100);
          onProgress({
            phase: 'loading',
            status: `Loading movies ${offset + 1}-${Math.min(offset + BATCH_SIZE, results.totalMovies)}...`,
            results
          });
        }

        const moviesResponse = await this.getFirebaseMovies(BATCH_SIZE, offset);
        const movies = moviesResponse.movies;
        
        if (!movies || movies.length === 0) {
          hasMore = false;
          break;
        }

        results.totalScanned += movies.length;
        
        // Scan this batch for suspicious movies
        const suspicious = this.identifySuspiciousMovies(movies);
        allSuspiciousMovies.push(...suspicious);
        results.totalSuspicious += suspicious.length;
        
        console.log(`[Fix ALL] Batch ${Math.floor(offset/BATCH_SIZE) + 1}: Loaded ${movies.length} movies, found ${suspicious.length} suspicious`);
        
        offset += BATCH_SIZE;
        hasMore = moviesResponse.hasMore;
        
        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      results.phases.loading.completed = true;
      results.phases.scanning.completed = true;

      console.log(`[Fix ALL] Phase 2 Complete: Scanned ${results.totalScanned} movies, found ${results.totalSuspicious} suspicious`);

      // Phase 3: Fix suspicious movies
      if (allSuspiciousMovies.length > 0) {
        console.log(`[Fix ALL] Phase 3: Fixing ${allSuspiciousMovies.length} suspicious movies...`);
        
        const movieIds = allSuspiciousMovies.map(movie => movie.id);
        const fixResults = await this.batchRefreshMetadata(movieIds, (progress) => {
          results.phases.fixing.progress = (progress.current / progress.total) * 100;
          if (onProgress) {
            onProgress({
              phase: 'fixing',
              status: `Fixing suspicious movie ${progress.current}/${progress.total}: ${progress.movieId}`,
              results
            });
          }
        });

        results.totalFixed = fixResults.successful;
        results.totalFailed = fixResults.failed;
        results.errors = fixResults.errors;
      }

      results.phases.fixing.completed = true;
      
      console.log(`[Fix ALL] COMPLETE! Scanned: ${results.totalScanned}, Suspicious: ${results.totalSuspicious}, Fixed: ${results.totalFixed}, Failed: ${results.totalFailed}`);
      
      return results;

    } catch (error) {
      console.error(`[Fix ALL] Error during progressive fix:`, error);
      results.errors.push({ phase: 'general', error: error.message });
      throw error;
    }
  }

  // Helper method to identify suspicious movies (used by fixAllMoviesProgressive)
  identifySuspiciousMovies(movies) {
    // Import the function from contentFilter - we'll need to make this available
    // For now, implement basic suspicious detection inline
    return movies.filter(movie => {
      const rating = movie.imdbRating || movie.tmdbRating || movie.ratings?.imdbRating || movie.ratings?.tmdbRating;
      if (!rating || rating <= 0) return false;
      
      const year = movie.year || movie.metadata?.year;
      const title = movie.title?.toLowerCase() || '';
      
      // Basic suspicious criteria
      const hasInvalidYear = !year || year < 1900 || year > new Date().getFullYear() + 1;
      const hasHighRating = rating >= 9.8;
      const hasWholeNumber = rating % 1 === 0 && rating >= 7.0;
      const hasCommonFakeRating = [7.0, 7.5, 8.0, 8.5, 9.0, 9.5].includes(rating);
      
      return hasInvalidYear || hasHighRating || hasWholeNumber || hasCommonFakeRating;
    });
  }

  async deleteFirebaseMovie(movieId) {
    const url = `${this.baseURL}/firebase/delete-movie?movieId=${encodeURIComponent(movieId)}`;

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Delete failed');
      }

      return fullResponse;
    } catch (error) {
      console.error('Delete movie failed:', error);
      throw error;
    }
  }

  async cleanupDuplicates() {
    const url = `${this.baseURL}/firebase/cleanup-duplicates`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Cleanup failed');
      }

      return fullResponse;
    } catch (error) {
      console.error('Cleanup duplicates failed:', error);
      throw error;
    }
  }

  async backfillGenres() {
    const url = `${this.baseURL}/firebase/movies`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Backfill failed');
      }

      return fullResponse;
    } catch (error) {
      console.error('Backfill genres failed:', error);
      throw error;
    }
  }

  // Manual scraping workflow
  async triggerScrape(providerUrls = null) {
    // Call the ENHANCED scrape endpoint with breakdown and debug info
    const url = `${this.baseURL}/scrape`;

    // Prepare request body
    const requestBody = {};
    if (providerUrls) {
      requestBody.providerUrls = providerUrls;
      console.log('🚀 Sending provider URLs with scrape request:', providerUrls);
    }

    try {
      console.log('🚀 Calling enhanced scrape endpoint:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : undefined
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();
      console.log('🚀 Enhanced API response received:', {
        success: fullResponse.success,
        hasBreakdown: !!fullResponse.breakdown,
        hasDebugInfo: !!fullResponse.debugInfo,
        dataKeys: Object.keys(fullResponse.data || {})
      });

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Scrape request failed');
      }

      // Return the FULL enhanced response
      return fullResponse;
    } catch (error) {
      console.error('Enhanced scrape request failed:', error);
      throw error;
    }
  }

  // Load provider configurations from database
  async loadProviderConfigs() {
    const url = `${this.baseURL}/scrape`;

    try {
      console.log('📖 Loading provider configurations from database...');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Failed to load provider configurations');
      }

      console.log(`✅ Loaded ${fullResponse.count} provider configurations from database`);
      return fullResponse;
    } catch (error) {
      console.error('Load provider configs failed:', error);
      // Return empty configs on failure so app doesn't break
      return { 
        success: false, 
        error: error.message,
        configs: {},
        count: 0
      };
    }
  }

  async saveScrapedToFirebase(data, batchOptions = null) {
    const url = `${this.baseURL}/firebase/save-scraped`;

    // Prepare request body
    const requestBody = { data };
    
    // Add batch parameters if provided
    if (batchOptions) {
      requestBody.batchIndex = batchOptions.batchIndex;
      requestBody.batchSize = batchOptions.batchSize;
      requestBody.totalBatches = batchOptions.totalBatches;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const fullResponse = await response.json();

      if (!fullResponse.success) {
        throw new Error(fullResponse.error || 'Save to Firebase failed');
      }

      return fullResponse; // Returns { success, results: { total, created, updated, errors } }
    } catch (error) {
      console.error('Save to Firebase failed:', error);
      throw error;
    }
  }

  // Transform scraped data to match Netflix clone format
  transformContentForNetflix(scrapedContent) {
    return scrapedContent.map(item => ({
      ...item,
      // Ensure all required fields are present
      thumbnailUrl: item.thumbnailUrl || `https://via.placeholder.com/300x400/000000/FFFFFF?text=${encodeURIComponent(item.title)}`,
      bannerUrl: item.bannerUrl || `https://via.placeholder.com/1920x1080/000000/FFFFFF?text=${encodeURIComponent(item.title)}`,
      description: item.description || `${item.title} - A ${item.type === 'movie' ? 'movie' : 'TV show'} from ${item.year}.`,
      cast: item.cast || ['Unknown Actor'],
      genre: item.genre || ['Unknown'],
      rating: item.rating || null,
      duration: item.duration || (item.type === 'movie' ? '120 min' : '45 min')
    }));
  }

  // Transform Firebase data to match Netflix clone format
  transformFirebaseToNetflix(firebaseMovies) {
    if (!firebaseMovies || !Array.isArray(firebaseMovies)) {
      return [];
    }

    // Deduplicate: prefer entries WITH genres
    const deduped = new Map();
    firebaseMovies.forEach(movie => {
      const existing = deduped.get(movie.id);
      const hasGenres = (movie.metadata?.genres?.length || 0) > 0 || (movie.genre?.length || 0) > 0;
      const existingHasGenres = existing ? ((existing.metadata?.genres?.length || 0) > 0 || (existing.genre?.length || 0) > 0) : false;

      // Keep this movie if:
      // 1. No existing entry, OR
      // 2. This one has genres and existing doesn't, OR
      // 3. Both have genres but this one has more complete data
      if (!existing || (hasGenres && !existingHasGenres)) {
        deduped.set(movie.id, movie);
      } else if (hasGenres && existingHasGenres) {
        // Both have genres, prefer one with more sources
        const thisSources = movie.scraped?.sources?.length || 0;
        const existingSources = existing.scraped?.sources?.length || 0;
        if (thisSources > existingSources) {
          deduped.set(movie.id, movie);
        }
      }
    });

    console.log(`[ApiService] Deduplication: ${firebaseMovies.length} → ${deduped.size} movies`);

    return Array.from(deduped.values()).map(movie => {
      // Get the first available source (or null if no sources)
      const firstSource = movie.scraped?.sources?.[0] || null;

      return {
        id: movie.id,
        title: movie.title,
        type: movie.type || 'movie',
        year: movie.metadata?.year || null,

        // Source info (from first source)
        source: firstSource?.sourceName || 'Unknown',
        watchseriesUrl: firstSource?.url || null,
        url: firstSource?.url || null,

        // Images - use Firebase posterUrl as thumbnailUrl
        thumbnailUrl: movie.metadata?.posterUrl || `https://via.placeholder.com/300x400/000000/FFFFFF?text=${encodeURIComponent(movie.title)}`,
        bannerUrl: movie.metadata?.posterUrl || movie.metadata?.bannerUrl || `https://via.placeholder.com/300x400/000000/FFFFFF?text=${encodeURIComponent(movie.title)}`,

        // Metadata
        description: movie.metadata?.plot || `${movie.title} is a ${movie.metadata?.genres?.[0]?.toLowerCase() || movie.type} from ${movie.metadata?.year || 'unknown'} available on ${firstSource?.sourceName || 'streaming platforms'}.`,
        genre: movie.metadata?.genres || movie.genre || [],
        rating: movie.metadata?.rated || null,
        duration: movie.metadata?.runtime || (movie.type === 'movie' ? '120 min' : '45 min'),
        cast: movie.metadata?.actors || ['Unknown Actor'],
        director: movie.metadata?.directors || [],

        // Ratings
        imdbRating: movie.ratings?.imdbRating || null,
        rottenTomatoes: movie.ratings?.rottenTomatoes || null,
        metacritic: movie.ratings?.metacritic || null,

        // Additional info for multi-source display
        sources: (movie.scraped?.sources || []).map(src => ({
          source: src.sourceName || src.source || 'Unknown',
          url: src.url
        })),
        sourceCount: movie.scraped?.sources?.length || 0
      };
    });
  }

  // Get content by category for Netflix rows
  async getContentByCategory(category) {
    switch (category.toLowerCase()) {
      case 'trending':
      case 'trending now':
        return this.getTrendingContent();

      case 'movies':
      case 'latest movies':
        return this.getLatestMovies();

      case 'tv shows':
      case 'tvshows':
      case 'latest tv shows':
        return this.getLatestTVShows();

      case 'action':
        const actionContent = await this.getTrendingContent();
        return actionContent.filter(item =>
          item.genre.some(g => g.toLowerCase().includes('action'))
        );

      case 'drama':
        const dramaContent = await this.getTrendingContent();
        return dramaContent.filter(item =>
          item.genre.some(g => g.toLowerCase().includes('drama'))
        );

      case 'sci-fi':
      case 'scifi':
        const scifiContent = await this.getTrendingContent();
        return scifiContent.filter(item =>
          item.genre.some(g => g.toLowerCase().includes('sci'))
        );

      case 'crime':
        const crimeContent = await this.getTrendingContent();
        return crimeContent.filter(item =>
          item.genre.some(g => g.toLowerCase().includes('crime'))
        );

      default:
        return this.getTrendingContent();
    }
  }

  // Simulate getting content by IDs (for compatibility with existing Redux structure)
  async getContentByIds(ids) {
    try {
      const allContent = await this.getTrendingContent();
      return allContent.filter(item => ids.includes(item.id));
    } catch (error) {
      console.error('Failed to get content by IDs:', error);
      return [];
    }
  }
}

export default new ApiService();